import { IconButton, Popper } from "@mui/material";
import classNames from "classnames";
import * as React from "react";

import { RobotoTooltip } from "@/shared/components/RobotoTooltip";

import type { HoverAction } from "./hoverAction";
import styles from "./HoverActionToolbar.module.css";

interface HoverActionToolbarProps {
  actions: readonly HoverAction[];
  /**
   * Extra class(es) for the inline wrapper around `children`. The wrapper
   * carries `data-open` while the toolbar shows, for hover styling that must
   * outlast CSS `:hover` once the pointer is over the portaled toolbar.
   */
  className?: string;
}

/**
 * Floats a row of icon buttons above `children` while the pointer is over
 * them — the affordance every rendering of an instant in AI markdown shares,
 * whether it offers one action (set as t=0) or three.
 *
 * The toolbar is portaled out of the prose, which is what makes it safe to
 * hang off text: nothing is injected into the flow to shift the line, be
 * caught by a text selection, or nest a `<button>` inside an `<a>`. It opens
 * centred on where the pointer entered rather than on the text's midpoint, so
 * it lands under the pointer even when the wrapped text wraps across lines.
 *
 * With no actions the children render bare — no wrapper listeners, no popup —
 * so a caller can pass an empty list rather than branching itself.
 *
 * Pointer-only by design, matching the visualize/jump toolbar it generalises:
 * the popup is portaled outside the wrapper's DOM subtree, so focus moving
 * into it reads as focus leaving the wrapper and there is no cheap
 * focus-within opener to add.
 */
export const HoverActionToolbar: React.FC<
  React.PropsWithChildren<HoverActionToolbarProps>
> = ({ actions, className, children }) => {
  const wrapperRef = React.useRef<HTMLSpanElement>(null);
  const [open, setOpen] = React.useState(false);
  const [mouseX, setMouseX] = React.useState(0);

  const close = React.useCallback(() => {
    setOpen(false);
  }, []);

  const handleMouseEnter = React.useCallback(
    (event: React.MouseEvent<HTMLSpanElement>) => {
      const wrapper = wrapperRef.current;
      if (wrapper === null) {
        return;
      }
      setMouseX(event.clientX - wrapper.getBoundingClientRect().left);
      setOpen(true);
    },
    [],
  );

  if (actions.length === 0) {
    return <>{children}</>;
  }

  return (
    <>
      <span
        ref={wrapperRef}
        className={classNames(styles.wrapper, className)}
        data-open={open || undefined}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={close}
      >
        {children}
      </span>
      {/* The Popper needs a live anchor element, which the ref only holds
          after the first paint; the hover that opens the toolbar re-renders
          this, so the guard costs nothing but the initial mount. */}
      {wrapperRef.current !== null && (
        <Popper
          open={open}
          anchorEl={wrapperRef.current}
          placement="top"
          modifiers={[
            {
              name: "offset",
              options: {
                offset: [
                  mouseX - wrapperRef.current.offsetWidth / 2,
                  // Enough of a gap that the toolbar doesn't sit on the text
                  // it describes; `.bridge::before` spans it for the pointer.
                  4,
                ],
              },
            },
          ]}
        >
          <div
            className={styles.bridge}
            onMouseEnter={() => {
              setOpen(true);
            }}
            onMouseLeave={close}
          >
            <div className={styles.toolbar}>
              {actions.map((action) => (
                <RobotoTooltip
                  key={action.key}
                  title={action.tooltip}
                  placement="top"
                >
                  {/* A disabled button fires no pointer events, so the
                      tooltip needs an enabled element to listen on. */}
                  <span>
                    <IconButton
                      size="small"
                      aria-label={action.tooltip}
                      disabled={action.disabled}
                      onClick={() => {
                        action.onClick();
                        close();
                      }}
                    >
                      {action.icon}
                    </IconButton>
                  </span>
                </RobotoTooltip>
              ))}
            </div>
          </div>
        </Popper>
      )}
    </>
  );
};
